/**
 * CR055: Source Health Dashboard
 */

import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { RefreshCw, AlertTriangle, CheckCircle2, XCircle, Activity, Pause, Pencil } from "lucide-react";
import { useSourceHealth } from "@/hooks/useSourceHealth";
import { useMerchantSources } from "@/hooks/useMerchantSources";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

export default function SourceHealth() {
  const { isAdmin } = useAuth();
  const {
    healthData,
    merchantHealth,
    loading,
    config,
    fetchHealthData,
    getFailingSources,
    getLowQualitySources,
    getMerchantsWithNoActiveSources,
    getMerchantsWithIssues,
  } = useSourceHealth();
  const { toggleStatus } = useMerchantSources();

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-muted-foreground">
          Admin access required
        </div>
      </AdminLayout>
    );
  }

  const failingSources = getFailingSources();
  const lowQualitySources = getLowQualitySources();
  const merchantsWithNoSources = getMerchantsWithNoActiveSources();
  const merchantsWithIssues = getMerchantsWithIssues();

  const hasIssues = failingSources.length > 0 || lowQualitySources.length > 0 || merchantsWithNoSources.length > 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Activity className="h-6 w-6" />
              Source Health Dashboard
            </h1>
            <p className="text-muted-foreground">
              Monitor source reliability over the last {config.lookback_hours} hours.
              Failure threshold: {config.failure_threshold_pct}%
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchHealthData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/merchant-sources">
                Manage Sources
              </Link>
            </Button>
          </div>
        </div>

        {/* Health Summary */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Sources Checked
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {healthData.filter((s) => s.total_checks > 0).length}
              </div>
            </CardContent>
          </Card>
          <Card className={failingSources.length > 0 ? "border-destructive" : ""}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <XCircle className="h-4 w-4 text-destructive" />
                Failing Sources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${failingSources.length > 0 ? "text-destructive" : ""}`}>
                {failingSources.length}
              </div>
              <p className="text-xs text-muted-foreground">
                &gt;{config.failure_threshold_pct}% failure rate
              </p>
            </CardContent>
          </Card>
          <Card className={lowQualitySources.length > 0 ? "border-orange-500" : ""}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Low Quality Sources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${lowQualitySources.length > 0 ? "text-orange-500" : ""}`}>
                {lowQualitySources.length}
              </div>
              <p className="text-xs text-muted-foreground">
                Avg text &lt;{config.min_text_length_success} chars
              </p>
            </CardContent>
          </Card>
          <Card className={merchantsWithNoSources.length > 0 ? "border-orange-500" : ""}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Merchants w/o Active
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${merchantsWithNoSources.length > 0 ? "text-orange-500" : ""}`}>
                {merchantsWithNoSources.length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* All Clear Message */}
        {!hasIssues && !loading && (
          <Card className="border-green-500 bg-green-50 dark:bg-green-950/20">
            <CardContent className="py-6">
              <div className="flex items-center justify-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">All sources healthy!</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Failing Sources */}
        {failingSources.length > 0 && (
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Failing Sources (&gt;{config.failure_threshold_pct}% failure rate)
              </CardTitle>
              <CardDescription>
                These sources are failing frequently and may need attention.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Merchant</TableHead>
                    <TableHead>Source URL</TableHead>
                    <TableHead>Failure Rate</TableHead>
                    <TableHead>Total Checks</TableHead>
                    <TableHead>Common Failures</TableHead>
                    <TableHead>Last Checked</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {failingSources.map((source) => (
                    <TableRow key={source.source_id}>
                      <TableCell className="font-medium">{source.merchant_name}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate" title={source.source_url}>
                        {source.source_url}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={source.failure_rate} className="w-16 h-2" />
                          <span className="text-sm text-destructive font-medium">
                            {source.failure_rate.toFixed(0)}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{source.total_checks}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {source.common_failure_reasons.slice(0, 2).map((reason) => (
                            <Badge key={reason} variant="outline" className="text-xs">
                              {reason}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {source.last_checked_at
                          ? formatDistanceToNow(new Date(source.last_checked_at), { addSuffix: true })
                          : "Never"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleStatus(source.source_id, "active")}
                          >
                            <Pause className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to="/admin/merchant-sources">
                              <Pencil className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Low Quality Sources */}
        {lowQualitySources.length > 0 && (
          <Card className="border-orange-500">
            <CardHeader>
              <CardTitle className="text-orange-500 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Low Quality Sources (Avg text &lt;{config.min_text_length_success} chars)
              </CardTitle>
              <CardDescription>
                These sources are returning very little text content.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Merchant</TableHead>
                    <TableHead>Source URL</TableHead>
                    <TableHead>Avg Text Length</TableHead>
                    <TableHead>Avg Quality Score</TableHead>
                    <TableHead>Total Checks</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lowQualitySources.map((source) => (
                    <TableRow key={source.source_id}>
                      <TableCell className="font-medium">{source.merchant_name}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate" title={source.source_url}>
                        {source.source_url}
                      </TableCell>
                      <TableCell className="text-orange-500 font-medium">
                        {source.avg_text_length.toFixed(0)} chars
                      </TableCell>
                      <TableCell>
                        {(source.avg_quality_score * 100).toFixed(0)}%
                      </TableCell>
                      <TableCell>{source.total_checks}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleStatus(source.source_id, "active")}
                          >
                            <Pause className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to="/admin/merchant-sources">
                              <Pencil className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Merchants with No Active Sources */}
        {merchantsWithNoSources.length > 0 && (
          <Card className="border-orange-500">
            <CardHeader>
              <CardTitle className="text-orange-500 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Merchants with No Active Sources
              </CardTitle>
              <CardDescription>
                These merchants have no active sources configured. Scans will fail.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {merchantsWithNoSources.map((merchant) => (
                  <Badge key={merchant.merchant_id} variant="outline" className="text-orange-500">
                    {merchant.merchant_name}
                  </Badge>
                ))}
              </div>
              <div className="mt-4">
                <Button asChild>
                  <Link to="/admin/merchant-sources">
                    Add Sources
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Merchants Health Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Merchant Health Overview</CardTitle>
            <CardDescription>
              Summary of source health by merchant
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Active Sources</TableHead>
                  <TableHead>Failing Sources</TableHead>
                  <TableHead>Low Quality Sources</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {merchantHealth
                  .filter((m) => m.active_sources > 0 || m.failing_sources > 0)
                  .slice(0, 20)
                  .map((merchant) => (
                    <TableRow key={merchant.merchant_id}>
                      <TableCell className="font-medium">{merchant.merchant_name}</TableCell>
                      <TableCell>{merchant.active_sources}</TableCell>
                      <TableCell className={merchant.failing_sources > 0 ? "text-destructive" : ""}>
                        {merchant.failing_sources}
                      </TableCell>
                      <TableCell className={merchant.low_quality_sources > 0 ? "text-orange-500" : ""}>
                        {merchant.low_quality_sources}
                      </TableCell>
                      <TableCell>
                        {merchant.failing_sources === 0 && merchant.low_quality_sources === 0 ? (
                          <Badge variant="default" className="bg-green-600">Healthy</Badge>
                        ) : merchant.failing_sources > 0 ? (
                          <Badge variant="destructive">Issues</Badge>
                        ) : (
                          <Badge variant="secondary">Low Quality</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
